import React, { useEffect, useState } from "react";
import ModalPopup from "components/common/atoms/ModalPopup";
import Button from "components/common/atoms/Button";
import LabelField from "components/common/atoms/LabelField";
import { useFormContext } from "components/CandidateCreation/context/Context";
import { t } from "components/CentralDataMangement/translation/Translation";
import {
  CvSuggestions,
  applyConflictValue,
  displayValue,
} from "../formData/cvSuggestions";

interface Props {
  show: boolean;
  suggestions: CvSuggestions | null;
  onClose: () => void;
}

/** Readable section headings in the popup. */
const SECTION_LABEL: Record<string, string> = {
  profileCard: "Profile",
  general: "General",
  jobProfile: "Desired job",
  other: "Other",
  internalInfo: "Internal info",
  workPermit: "Work permit",
};

/** camelCase field name → "Title case words" for display. */
const humanize = (field: string): string =>
  field
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();

/**
 * CR-576 — conflict review popup shown after a CV is parsed.
 *
 * Empty fields are auto-filled and brand-new entries (studies / experience /
 * languages / IT / soft skills) are auto-added BEFORE this opens — silently.
 * This popup therefore only handles the one thing that needs a decision:
 * a field whose existing value differs from the CV. The recruiter chooses
 * "Use CV" (overwrite) or "Keep". If there are no conflicts, it never opens.
 */
const ReviewCvSuggestionsModal: React.FC<Props> = ({ show, suggestions, onClose }) => {
  const { state, dispatch } = useFormContext();
  // Tracks the outcome chosen for each conflict row: id -> "Applied" | "Kept".
  const [resolved, setResolved] = useState<Record<string, string>>({});

  // Reset row outcomes whenever a fresh set of suggestions arrives.
  useEffect(() => {
    setResolved({});
  }, [suggestions]);

  if (!suggestions) return null;

  const conflicts = suggestions.conflicts;

  const mark = (id: string, outcome: string) =>
    setResolved((prev) => ({ ...prev, [id]: outcome }));

  const takeCvValue = (c: CvSuggestions["conflicts"][number]) => {
    applyConflictValue(c.section, c.field, c.parsed, dispatch, state);
    mark(`c:${c.section}.${c.field}`, t("Applied"));
  };
  const keepConflict = (c: CvSuggestions["conflicts"][number]) =>
    mark(`c:${c.section}.${c.field}`, t("Kept existing"));

  // "Apply all": take the CV value for every unresolved conflict, then close.
  const applyAll = () => {
    conflicts.forEach((c) => {
      if (!resolved[`c:${c.section}.${c.field}`]) takeCvValue(c);
    });
    onClose();
  };

  const body = (
    <div className="cv-review-body" style={{ maxHeight: "55vh", overflowY: "auto" }}>
      {suggestions.autoFillFields.length > 0 && (
        <p className="mb-3">
          {suggestions.autoFillFields.length}{" "}
          {t("empty field(s) were filled automatically from the CV.")}
        </p>
      )}

      {conflicts.length > 0 && (
        <div className="mb-2">
          <LabelField
            title={t("Conflicts existing data kept, please review")}
            className="tab-subtitle pb-0 marginBottomPoint5"
          />
          {conflicts.map((c) => {
            const id = `c:${c.section}.${c.field}`;
            return (
              <div
                key={id}
                className="d-flex align-items-center py-2 border-bottom"
              >
                {/* Field info grows on the left and wraps within its own column */}
                <div className="flex-grow-1 text-break pe-3">
                  <strong>
                    {t(SECTION_LABEL[c.section] ?? c.section)} ▸ {t(humanize(c.field))}
                  </strong>
                  <div className="mt-1">
                    <span className="text-muted">{t("Current")}: </span>
                    {displayValue(c.existing)}
                  </div>
                  <div>
                    <span className="text-muted">{t("From CV")}: </span>
                    {displayValue(c.parsed)}
                  </div>
                </div>
                {/* Actions pinned right, never wrap under the text */}
                <div className="d-flex flex-shrink-0">
                  {resolved[id] ? (
                    <span className="text-muted">{resolved[id]}</span>
                  ) : (
                    <>
                      <Button
                        title={t("Use CV")}
                        handleClick={() => takeCvValue(c)}
                        className="btn modal-confirm-btn shadow-none marginRightPoint5"
                      />
                      <Button
                        title={t("Keep")}
                        handleClick={() => keepConflict(c)}
                        className="btn modal-cancel-btn shadow-none"
                      />
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <ModalPopup
      show={show}
      size="lg"
      onHide={onClose}
      title={t("Review information from the uploaded CV")}
      body={body}
      closeTitle={t("Close")}
      onCloseButtonClick={onClose}
      confirmTitle={t("Apply all")}
      onConfirmButtonClick={applyAll}
      className="review-information-modal"
    />
  );
};

export default ReviewCvSuggestionsModal;
